import { emailIsValid } from "utils";

import {
  DIRECT_BOOKING_MESSAGE,
  getDirectBookingAssigneeError,
  getDirectBookingPriceError,
} from "../../../../utils";

import type { DirectBookingAssignmentFormState } from "types/shipment";

import type { DirectBookingAssigneeCheck } from "../../../../utils";
import type { NetworkMember, PartnerSelectionItem } from "../../types";

function getFullName(member: NetworkMember): string {
  return `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim();
}

export function getNetworkMemberOrNone(
  email: string,
  networkMembers: NetworkMember[],
): NetworkMember | null {
  const target = email.toLowerCase();
  return (
    networkMembers.find((member) => member.email.toLowerCase() === target) ??
    null
  );
}

// Network partners only: no "All my partners", no groups, no pending invites.
export function createAssigneeOptions(
  networkMembers: NetworkMember[],
): PartnerSelectionItem[] {
  return networkMembers.map((member) => ({
    type: "partner" as const,
    email: member.email,
    name: getFullName(member),
  }));
}

// The chip for the current assignee: a partner chip when the email is in the
// network, an email chip otherwise. Null while no carrier is picked.
export function getAssigneeSelectionItem(
  assignment: DirectBookingAssignmentFormState,
  networkMembers: NetworkMember[],
): PartnerSelectionItem | null {
  if (assignment.carrierEmail === "") {
    return null;
  }
  const member = getNetworkMemberOrNone(
    assignment.carrierEmail,
    networkMembers,
  );
  if (member !== null) {
    return {
      type: "partner",
      email: assignment.carrierEmail,
      name: getFullName(member),
    };
  }
  if (assignment.carrierDisplayName !== null) {
    return {
      type: "partner",
      email: assignment.carrierEmail,
      name: assignment.carrierDisplayName,
    };
  }
  return { type: "email", email: assignment.carrierEmail };
}

interface AssignmentFromEmailProps {
  email: string;
  networkMembers: NetworkMember[];
  current: DirectBookingAssignmentFormState;
}

// Puts a committed email on the assignment, keeping the rate. Null when the
// email is not valid.
export function buildAssignmentFromEmail(
  props: AssignmentFromEmailProps,
): DirectBookingAssignmentFormState | null {
  const { email, networkMembers, current } = props;
  const trimmed = email.trim();
  if (!emailIsValid(trimmed)) {
    return null;
  }
  const normalized = trimmed.toLowerCase();
  const member = getNetworkMemberOrNone(normalized, networkMembers);
  return {
    ...current,
    carrierEmail: normalized,
    carrierDisplayName: member === null ? null : getFullName(member),
  };
}

interface AssignmentFromSelectionProps {
  // The autocomplete's new value; only the last pick is kept.
  selections: (string | PartnerSelectionItem)[];
  networkMembers: NetworkMember[];
  current: DirectBookingAssignmentFormState;
}

// Resolves the autocomplete's selection to one carrier. A second pick replaces
// the first; clearing the chips clears the carrier but keeps the rate.
export function buildAssignmentFromSelection(
  props: AssignmentFromSelectionProps,
): DirectBookingAssignmentFormState | null {
  const { selections, networkMembers, current } = props;
  const last = selections[selections.length - 1];
  if (last === undefined) {
    return { ...current, carrierEmail: "", carrierDisplayName: null };
  }
  if (typeof last === "string") {
    return buildAssignmentFromEmail({ email: last, networkMembers, current });
  }
  if (last.type === "partner" || last.type === "email") {
    return buildAssignmentFromEmail({
      email: last.email,
      networkMembers,
      current,
    });
  }
  return null;
}

// A project's rate applies to each of its loads.
export function getAssigneePriceHelperText(isProject: boolean): string | null {
  return isProject ? "For each load" : null;
}

// The price row's error, from the rule the form validates with, once the
// shipper has pressed Review.
export function getAssigneePriceError(
  assignment: DirectBookingAssignmentFormState,
  reviewAttempted: boolean,
): string | null {
  return reviewAttempted ? getDirectBookingPriceError(assignment) : null;
}

export interface AssigneeStatus {
  kind: "checking" | "info" | "error";
  message: string;
}

/*
The line under the picker. It reads the same rules the form validates with, so
the line and the Review button never disagree. A carrier in the network says
nothing unless the check rejects them - a blocked partner is still a partner.
A missing carrier is only flagged once the shipper has pressed Review.
*/
export function getAssigneeStatus(
  assignment: DirectBookingAssignmentFormState,
  assigneeCheck: DirectBookingAssigneeCheck,
  reviewAttempted: boolean,
): AssigneeStatus | null {
  if (assignment.carrierEmail === "") {
    return reviewAttempted
      ? { kind: "error", message: DIRECT_BOOKING_MESSAGE.CARRIER_REQUIRED }
      : null;
  }
  if (assigneeCheck.inNetwork) {
    const rejection = getDirectBookingAssigneeError(assignment, assigneeCheck);
    return rejection === null ? null : { kind: "error", message: rejection };
  }
  if (assigneeCheck.isLoading) {
    return {
      kind: "checking",
      message: DIRECT_BOOKING_MESSAGE.CARRIER_CHECK_PENDING,
    };
  }
  const error = getDirectBookingAssigneeError(assignment, assigneeCheck);
  if (error === DIRECT_BOOKING_MESSAGE.CARRIER_CHECK_PENDING) {
    return null;
  }
  if (error !== null) {
    return { kind: "error", message: error };
  }
  return { kind: "info", message: DIRECT_BOOKING_MESSAGE.NOT_IN_NETWORK };
}
